import { getBrand } from '@/lib/branding';
import { useIsTouchDevice } from '@/lib/utils/deviceDetection';

/**
 * Full-page "download to play" gate shown to public web visitors who reach
 * the game routes (/play, /labs). The playable web shell isn't exposed to
 * the public — this converts the visitor into an app install instead of
 * letting them hit a half-broken web game.
 *
 * Store URLs mirror DownloadSection.tsx (kept in sync deliberately) so the
 * attribution params still flow into App Store Connect / Play Console.
 */
const APP_STORE_URL =
  'https://apps.apple.com/us/app/poker-blitz/id6754580452?ct=web_play_gate&pt=pokerblitzgame&mt=8';
const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.pokerblitz.app' +
  '&referrer=' +
  encodeURIComponent(
    'utm_source=pokerblitzgame&utm_medium=web&utm_campaign=play_gate'
  );

/**
 * Device-routing short link served by api/get.ts. The QR below encodes
 * this URL — a phone camera scan lands here and gets 302'd to the right
 * store with qr_gate attribution. Also used in the mailto fallback.
 */
const GET_APP_LINK = 'https://pokerblitzgame.com/api/get';

function trackStoreClick(store: 'apple' | 'google') {
  try {
    const w = window as unknown as { gtag?: (...args: unknown[]) => void };
    w.gtag?.('event', 'store_click', {
      store,
      destination_url: store === 'apple' ? APP_STORE_URL : PLAY_STORE_URL,
      source: 'web_play_gate',
    });
  } catch {
    /* analytics is best-effort */
  }
}

function trackEmailSelfClick() {
  try {
    const w = window as unknown as { gtag?: (...args: unknown[]) => void };
    w.gtag?.('event', 'email_self_link_click', {
      destination_url: GET_APP_LINK,
      source: 'web_play_gate',
    });
  } catch {
    /* analytics is best-effort */
  }
}

export function DownloadToPlayGate() {
  const brand = getBrand();
  // Touch devices (phones/tablets) can tap the store badges directly —
  // a QR code is useless when the camera is the screen you're looking
  // at. Only desktop visitors get the scan-to-install hero.
  const isTouch = useIsTouchDevice();

  const mailtoHref =
    'mailto:?subject=' +
    encodeURIComponent(`Get ${brand.appName}`) +
    '&body=' +
    encodeURIComponent(
      `Open this on your phone to install ${brand.appName}:\n\n${GET_APP_LINK}\n\nIt routes you straight to the right app store.`
    );

  return (
    <main className="min-h-screen flex flex-col items-center justify-center px-6 py-16 text-center bg-gradient-to-br from-gray-950 via-purple-950 to-gray-900 text-white">
      <div className="max-w-md w-full">
        <h1 className="text-3xl sm:text-4xl font-extrabold mb-4 bg-gradient-to-r from-yellow-300 via-pink-400 to-purple-400 bg-clip-text text-transparent">
          {brand.appName}
        </h1>
        <p className="text-xl sm:text-2xl font-bold mb-3">
          Play on iOS &amp; Android
        </p>
        <p className="text-sm sm:text-base text-white/70 mb-10">
          {brand.appName} is built for mobile. Grab the free app to match cards,
          build streaks, and climb the leaderboards — the full experience lives
          on your phone.
        </p>

        {!isTouch && (
          <div className="mb-10 flex flex-col items-center">
            <div className="bg-white rounded-2xl p-4 shadow-[0_0_40px_rgba(168,85,247,0.35)] ring-1 ring-white/20">
              <img
                src="/qr-get-app.svg"
                alt={`QR code — scan with your phone camera to download ${brand.appName}`}
                className="block h-[168px] w-[168px]"
                width={168}
                height={168}
                decoding="async"
              />
            </div>
            <p className="mt-4 text-base sm:text-lg font-semibold text-white">
              On your phone in 2 seconds
            </p>
            <p className="mt-1 text-sm text-white/60">
              Scan with your camera — it opens the right store automatically.
            </p>
            <a
              href={mailtoHref}
              className="mt-3 text-xs text-white/50 hover:text-white/80 underline underline-offset-4 transition-colors"
              onClick={trackEmailSelfClick}
            >
              No camera handy? Email yourself the link
            </a>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:opacity-80 transition-opacity"
            aria-label="Download on the App Store"
            onClick={() => trackStoreClick('apple')}
          >
            <img
              src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=172x60&releaseDate=2025-01-01"
              alt="Download on the App Store"
              className="h-[52px] w-auto"
              loading="lazy"
              decoding="async"
            />
          </a>
          <a
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:opacity-80 transition-opacity"
            aria-label="Get it on Google Play"
            onClick={() => trackStoreClick('google')}
          >
            <img
              src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
              alt="Get it on Google Play"
              className="h-[64px] w-auto"
              loading="lazy"
              decoding="async"
            />
          </a>
        </div>

        <a
          href="/?web=1"
          className="inline-block mt-10 text-sm text-white/60 hover:text-white underline underline-offset-4 transition-colors"
        >
          ← Back to home
        </a>
      </div>
    </main>
  );
}
